import { CONTENT_TOKEN_QUERY_PARAM } from "@/lib/api/v2/content-token-constants";

const API_V2_PATH_PREFIX = "/api/v2/";

/**
 * Strips the API v2 content token out of any URL on its way to Sentry (ENG-5145 design §9).
 *
 * A stamped `Attachment.url` carries its credential in the query string — the one exception to
 * the spec's §5.2 no-query-string-tokens rule, granted because off-the-shelf tooling
 * dereferences that field and cannot attach a header. The obligation that came with the
 * exception is that OUR OWN telemetry never records it.
 *
 * The v2 request pipeline already logs `pathname` rather than `request.url`, so the structured
 * logs are clean by construction. Sentry is the gap: its Next.js integration attaches the full
 * request URL to server events and to `http` breadcrumbs automatically, so a single 5xx on a
 * stamped-URL fetch would file the live credential into an issue. That is not theoretical — it
 * is the default behavior, and nothing in the route code has to be wrong for it to happen.
 *
 * Scoped to `/api/v2/` but NOT to the attachment routes specifically, and the breadth is
 * deliberate: over-stripping costs a debugging parameter, under-stripping logs a PHI
 * credential. v2 publishes no other `t` parameter, so there is nothing else to lose.
 */
export function redactContentToken(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    if (!url.startsWith(API_V2_PATH_PREFIX)) return url;
    parsed = new URL(url, "http://localhost");
    if (!parsed.searchParams.has(CONTENT_TOKEN_QUERY_PARAM)) return url;

    parsed.searchParams.set(CONTENT_TOKEN_QUERY_PARAM, "[redacted]");
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }

  if (!parsed.pathname.startsWith(API_V2_PATH_PREFIX)) return url;
  if (!parsed.searchParams.has(CONTENT_TOKEN_QUERY_PARAM)) return url;

  parsed.searchParams.set(CONTENT_TOKEN_QUERY_PARAM, "[redacted]");
  return parsed.toString();
}

/** Shape of the Sentry event fields this scrub touches. Structural, to avoid the SDK types. */
interface EventWithRequest {
  request?: { url?: string; query_string?: unknown };
  spans?: { data?: Record<string, unknown>; description?: string }[];
  contexts?: { trace?: { data?: Record<string, unknown> } };
}

/** Shape of the breadcrumb fields this scrub touches. Structural, to avoid the SDK types. */
interface BreadcrumbWithData {
  data?: Record<string, unknown>;
}

/**
 * Span/transaction attributes that carry a request URL. Names come from the OpenTelemetry HTTP
 * semantic conventions plus Sentry's own older spellings, and BOTH generations are listed
 * because the SDK emits whichever its instrumentation was built against. `http.target` and
 * `url.path` are relative rather than absolute — they still carry the query string, which is
 * the part that matters here.
 */
const URL_BEARING_SPAN_ATTRIBUTES = [
  "http.url",
  "url.full",
  "http.target",
  "url.path",
  "url.query",
  "server.address",
];

const INNGEST_EVENT_SPAN_ATTRIBUTE = "inngest.event";

/**
 * Span attributes that carry LLM prompt or response content (ENG-5590).
 *
 * AI SDK spans reach Langfuse and Sentry through one OpenTelemetry provider. Langfuse holds a
 * BAA; Sentry does not. The chat system prompt renders the patient name and date of birth, so
 * `gen_ai.system_instructions` carries PHI on every chat turn.
 *
 * `recordInputs: false` is not the fix — one span pipeline feeds both vendors, so it would
 * blind Langfuse too, where the BAA makes the capture legitimate.
 *
 * The list is exact, never a `gen_ai.*` prefix sweep. Model name, token counts, and
 * `gen_ai.operation.name` carry no content and are what make an AI span worth reading.
 */
const AI_CONTENT_SPAN_ATTRIBUTES = [
  "gen_ai.system_instructions",
  "gen_ai.input.messages",
  "gen_ai.output.messages",
  "gen_ai.tool.call.arguments",
  "gen_ai.tool.call.result",
  "ai.prompt",
  "ai.prompt.messages",
  "ai.prompt.tools",
  "ai.response.text",
  "ai.response.object",
  "ai.response.reasoning",
  "ai.response.toolCalls",
  "ai.toolCall.args",
  "ai.toolCall.result",
];

/** Percent-encodes every character, including the ones that need no encoding. */
function percentEncodeAll(value: string): string {
  return Array.from(value)
    .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`)
    .join("");
}

/**
 * Every spelling of the parameter name that reaches the SAME parameter.
 *
 * `URLSearchParams` percent-decodes a parameter's name before matching it, so `%74=` IS `t=` as
 * far as the resolver is concerned — and the resolver is what decides whether a presented token
 * is honored. A scrub that recognizes only the literal spelling leaves the encoded one intact on
 * span attributes and descriptions, which is a live credential published on the success path.
 * The rule is that this net stays exactly as liberal as the thing it protects.
 *
 * Case is not a variable here: the encoded form of `t` is `%74`, whose hex digits carry no
 * letters. A case-insensitive match would be wrong anyway — `T` is a DIFFERENT parameter, and
 * the resolver does not accept it.
 */
const CONTENT_TOKEN_PARAM_SPELLINGS = [
  CONTENT_TOKEN_QUERY_PARAM,
  percentEncodeAll(CONTENT_TOKEN_QUERY_PARAM),
];

/**
 * Reused across calls rather than rebuilt per value. `String.replace` resets a global regex's
 * `lastIndex` on every call, so sharing one instance is safe.
 */
const CONTENT_TOKEN_ANYWHERE_PATTERN = new RegExp(
  `(^|[?&])(?:${CONTENT_TOKEN_PARAM_SPELLINGS.join("|")})=[^&\\s]*`,
  "g",
);

/**
 * Whether a request URL addresses the v2 partner surface.
 *
 * Both spellings are accepted because the SDK emits either one depending on the integration
 * that populated `request.url` — an absolute URL from the request normalizer, a bare path from
 * others. A relative value is not parseable as a URL, so it is prefix-tested directly.
 */
function isApiV2RequestUrl(url: string): boolean {
  try {
    return new URL(url).pathname.startsWith(API_V2_PATH_PREFIX);
  } catch {
    return url.startsWith(API_V2_PATH_PREFIX);
  }
}

/**
 * Removes sensitive request and Inngest data from an outgoing Sentry event, in place.
 *
 * `query_string` is dropped outright rather than rewritten: it arrives in several shapes
 * (string, record, tuple array) depending on the integration that populated it, and a
 * partial rewrite that missed one would be a silent leak. Nothing on the v2 surface needs it
 * for triage — the structured log line carries the request id, practice, and key.
 *
 * THE DROP IS KEYED ON THE PATH, NOT ON THE URL HAVING CHANGED. The SDK captures the URL and
 * its query string as SEPARATE fields, and the Next.js transaction processor then replaces the
 * URL with a query-free one while retaining that separate query string. A guard that fired only
 * when redaction changed the URL therefore never fired on the one shape that leaks: the hook
 * sees no token in the URL, leaves the query string alone, and the live credential ships. That
 * path has no response-status condition either, so an ORDINARY SUCCESSFUL attachment request
 * takes it.
 */
export function redactSensitiveDataFromEvent<T extends EventWithRequest>(event: T): T {
  if (event.request && typeof event.request.url === "string") {
    event.request.url = redactContentToken(event.request.url);
    if (isApiV2RequestUrl(event.request.url)) delete event.request.query_string;
  }

  redactSpanAttributes(event.contexts?.trace?.data);
  for (const span of event.spans ?? []) redactSensitiveDataFromSpan(span);

  return event;
}

/** Shape of the span payload `beforeSendSpan` hands over. Structural, to avoid the SDK types. */
interface SpanWithData {
  data?: Record<string, unknown>;
  description?: string;
}

/**
 * Scrubs one span, in place — the `beforeSendSpan` hook, and the per-span step of the event walk.
 *
 * A child span can leave through the span envelope rather than the transaction exporter, and
 * `beforeSendTransaction` never sees that one.
 */
export function redactSensitiveDataFromSpan<T extends SpanWithData>(span: T): T {
  redactSpanAttributes(span.data);

  // The shape-based scrub, not the URL-parsed one: a description is `GET /path?query`, which
  // is not a parseable absolute URL, so the strict version would return it unchanged.
  if (typeof span.description === "string") {
    span.description = redactContentTokenAnywhere(span.description);
  }

  return span;
}

/**
 * Scrubs sensitive attributes on one span or on the transaction's root trace context.
 *
 * `beforeSend` DOES NOT SEE TRANSACTIONS, which is the gap this closes. With tracing on, every
 * successful request to a stamped URL produces a transaction whose root context and HTTP spans
 * carry the full URL — no error required, so the ordinary success path was publishing live PHI
 * credentials to Sentry while the error path was clean.
 *
 * Attribute values are matched loosely (any string containing the token parameter) rather than
 * parsed as URLs, because the relative spellings (`http.target`, `url.path`) are not parseable
 * origins and `url.query` is a bare query string.
 */
function redactSpanAttributes(data?: Record<string, unknown>): void {
  if (!data) return;

  // Inngest adds the full serialized event. Separate attributes retain its name and ID.
  if (Object.hasOwn(data, INNGEST_EVENT_SPAN_ATTRIBUTE)) {
    data[INNGEST_EVENT_SPAN_ATTRIBUTE] = "[redacted]";
  }

  // Deleted, not marked: a placeholder value on a prompt attribute reads like captured content.
  for (const attribute of AI_CONTENT_SPAN_ATTRIBUTES) delete data[attribute];

  for (const attribute of URL_BEARING_SPAN_ATTRIBUTES) {
    const value = data[attribute];
    if (typeof value === "string") data[attribute] = redactContentTokenAnywhere(value);
  }
}

/**
 * Token scrub for values that are not necessarily absolute URLs: a relative path, a bare query
 * string, or a span description like `GET /api/v2/documents/x/attachments/0?t=…`.
 *
 * Deliberately shape-based rather than URL-parsed. `redactContentToken` above stays the
 * precise path-scoped version for real URLs; this one is the net underneath it, and the
 * asymmetry is intentional — over-redacting a span attribute costs a debugging string, while
 * under-redacting publishes a PHI credential.
 *
 * THE PARAMETER MAY START THE VALUE, which is not an edge case: OpenTelemetry's `url.query` is
 * the query string WITHOUT its leading `?`, so a single-parameter stamped URL arrives here as a
 * bare `t=…`. A pattern anchored on `?` or `&` alone matches nothing there and leaves the live
 * token on the ordinary success-path transaction — the exact leak this module exists to close.
 * `^` needs no `m` flag: these values are single-line.
 */
export function redactContentTokenAnywhere(value: string): string {
  if (!CONTENT_TOKEN_PARAM_SPELLINGS.some((name) => value.includes(`${name}=`))) return value;
  return value.replace(CONTENT_TOKEN_ANYWHERE_PATTERN, `$1${CONTENT_TOKEN_QUERY_PARAM}=[redacted]`);
}

/** Breadcrumb fields carrying a bare query string rather than a whole URL. */
export const QUERY_BEARING_BREADCRUMB_FIELDS = ["http.query", "query_string"];

/**
 * Scrubs the token off one breadcrumb, in place.
 *
 * `http` breadcrumbs carry the URL separately from the event's request block, and the SDK splits
 * the query off into its own field the same way it does there — so scrubbing `data.url` alone
 * leaves the credential on the breadcrumb. The URL field keeps the precise path-scoped scrub;
 * the bare query fields take the shape-based one, since neither is a parseable URL.
 */
export function redactContentTokenFromBreadcrumb<T extends BreadcrumbWithData>(breadcrumb: T): T {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;

  if (typeof data.url === "string") data.url = redactContentToken(data.url);

  for (const field of QUERY_BEARING_BREADCRUMB_FIELDS) {
    const value = data[field];
    if (typeof value === "string") data[field] = redactContentTokenAnywhere(value);
  }

  return breadcrumb;
}
